import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Star, MapPin, Check, Calendar, Users, ChevronLeft, ChevronRight, Plus, Minus } from "lucide-react";
import api, { formatTRY, formatDate, resolveImageUrl } from "@/lib/api";
import { toLocalYMD, todayYMD, plusDaysYMD } from "@/lib/dateUtils";
import { AMENITY_CATEGORIES } from "@/lib/amenitiesCatalog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HotelMap from "@/components/HotelMap";
import NearbyPOIs from "@/components/NearbyPOIs";
import BlockRenderer from "@/components/BlockRenderer";
import AdSlot from "@/components/AdSlot";
import RatePlanComparison from "@/components/RatePlanComparison";
import { useSiteContent } from "@/lib/siteContent";
import { track } from "@/lib/analytics";

export default function HotelDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const site = useSiteContent();
    const tailBlocks = site?.hotel_detail_tail_blocks || [];
    const [hotel, setHotel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [galleryIdx, setGalleryIdx] = useState(0);
    const [reviewOpen, setReviewOpen] = useState(false);

    const today = todayYMD();
    const nextWeek = plusDaysYMD(7);

    const [checkIn, setCheckIn] = useState(params.get("check_in") || today);
    const [checkOut, setCheckOut] = useState(params.get("check_out") || nextWeek);
    const [adults, setAdults] = useState(Number(params.get("adults") || 2));
    const [children, setChildren] = useState(Number(params.get("children") || 0));
    const [childAges, setChildAges] = useState(() => {
        const parsed = (params.get("child_ages") || "").split(",").filter(Boolean).map(Number);
        return parsed.length ? parsed : [];
    });
    const [rooms, setRooms] = useState(Number(params.get("rooms") || 1));
    const [selectedRoomId, setSelectedRoomId] = useState(null);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [quote, setQuote] = useState(null);
    const [quoting, setQuoting] = useState(false);
    const [roomQuotes, setRoomQuotes] = useState({}); // room_id -> { total, per_night_avg, original_total }
    const [roomQuotesLoading, setRoomQuotesLoading] = useState(false);

    // Keep childAges length aligned with children count
    useEffect(() => {
        setChildAges((prev) => {
            const next = [...prev];
            while (next.length < children) next.push(5);
            while (next.length > children) next.pop();
            return next;
        });
    }, [children]);

    // When the user lands here from an embedded widget (ref=widget) we capture
    // the attribution params into sessionStorage; CheckoutPage later reads them
    // and includes them in the booking payload so the booking is tagged
    // source=widget with the originating hotel.
    useEffect(() => {
        if (params.get("ref") !== "widget") return;
        try {
            sessionStorage.setItem("msonline_widget_ref", JSON.stringify({
                source: "widget",
                variant: params.get("wv") || "",
                theme: params.get("wt") || "",
                referrer_hotel_id: params.get("rh") || id,
                ts: Date.now(),
            }));
        } catch (e) { /* private mode */ }
    }, [params, id]);

    useEffect(() => {
        setLoading(true);
        api.get(`/hotels/${id}`)
            .then((r) => {
                setHotel(r.data);
                track("hotel_view", { hotel_id: r.data.id, hotel_name: r.data.name });
                if (r.data.room_types?.length) {
                    const first = r.data.room_types[0];
                    setSelectedRoomId(first.id);
                    const cheapest = (first.rate_plans || []).find((p) => p.is_cheapest) || (first.rate_plans || [])[0];
                    setSelectedPlanId(cheapest?.id || null);
                }
            })
            .finally(() => setLoading(false));
    }, [id]);

    const nights = Math.max(1, Math.round((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)));
    const minCheckout = (() => {
        const d = new Date(checkIn || today);
        d.setDate(d.getDate() + 1);
        return toLocalYMD(d);
    })();
    const selectedRoom = hotel?.room_types?.find((r) => r.id === selectedRoomId);
    const selectedPlan = selectedRoom?.rate_plans?.find((p) => p.id === selectedPlanId);

    // Capacity check helper: returns { ok, reason } for a given room and current guest selection
    const checkCapacity = (rt) => {
        if (!rt) return { ok: false, reason: "Oda yok" };
        const cap = Number(rt.capacity || 2);
        const maxKids = Number(rt.max_children ?? 0);
        const total = adults + children;
        if (total > cap) return { ok: false, reason: `Kapasite aşıldı (max ${cap} kişi)` };
        if (maxKids > 0 && children > maxKids) return { ok: false, reason: `Bu odaya max ${maxKids} çocuk kabul edilir` };
        if (maxKids === 0 && children > 0) {
            // Default: if max_children not set, allow children up to capacity - 1 (need at least 1 adult)
            if (adults < 1) return { ok: false, reason: "En az 1 yetişkin gerekli" };
        }
        return { ok: true };
    };
    const selectedRoomFits = checkCapacity(selectedRoom).ok;

    // If selected room cannot accommodate the current guests, auto-pick the first room that fits
    useEffect(() => {
        if (!hotel?.room_types?.length) return;
        if (selectedRoom && checkCapacity(selectedRoom).ok) return;
        const next = hotel.room_types.find((r) => checkCapacity(r).ok);
        if (next && next.id !== selectedRoomId) {
            setSelectedRoomId(next.id);
            const cheapest = (next.rate_plans || []).find((p) => p.is_cheapest) || (next.rate_plans || [])[0];
            setSelectedPlanId(cheapest?.id || null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adults, children, hotel?.id]);
    // Static fallback price (used until quote loads)
    const fallbackPerNight = selectedPlan
        ? (adults <= 2 ? (selectedPlan.price_2 || selectedPlan.price_1) : (selectedPlan.price_3 || selectedPlan.price_2 || selectedPlan.price_1))
        : (selectedRoom?.price_per_night || 0);
    const fallbackTotal = fallbackPerNight * nights * rooms;
    const totalPrice = quote?.total ?? fallbackTotal;
    const pricePerNight = quote?.per_night_avg ?? fallbackPerNight;

    // Live quote — re-fetch when selection or guests change
    useEffect(() => {
        if (!hotel?.id || !selectedRoomId) return;
        if (!selectedRoomFits) {
            // Skip quote when room doesn't fit selected guests
            setQuote(null);
            setQuoting(false);
            return;
        }
        setQuoting(true);
        const t = setTimeout(async () => {
            try {
                const r = await api.post("/bookings/quote", {
                    hotel_id: hotel.id,
                    room_type_id: selectedRoomId,
                    rate_plan_id: selectedPlanId || null,
                    check_in: checkIn,
                    check_out: checkOut,
                    adults,
                    children,
                    child_ages: childAges,
                    rooms,
                    insurance: false,
                });
                setQuote(r.data);
            } catch (err) {
                // Capture stop-sale / no-availability messages from the backend so
                // the UI can show "SATIŞA KAPALI" instead of silently falling back
                // to a stale static price.
                const detail = err?.response?.data?.detail;
                setQuote({ _unavailable: true, _reason: typeof detail === "string" ? detail : "Bu tarihler için müsaitlik yok" });
            } finally {
                setQuoting(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [hotel?.id, selectedRoomId, selectedPlanId, checkIn, checkOut, adults, children, JSON.stringify(childAges), rooms, selectedRoomFits]);

    // Fetch real-time quote for every room that fits the selected guests (parallel)
    useEffect(() => {
        if (!hotel?.id || !hotel.room_types?.length) return;
        const eligibleRooms = hotel.room_types.filter((rt) => checkCapacity(rt).ok);
        if (!eligibleRooms.length) {
            setRoomQuotes({});
            return;
        }
        let cancelled = false;
        setRoomQuotesLoading(true);
        const t = setTimeout(async () => {
            try {
                const results = await Promise.all(
                    eligibleRooms.map(async (rt) => {
                        const cheap = (rt.rate_plans || []).find((p) => p.is_cheapest) || (rt.rate_plans || [])[0];
                        try {
                            const r = await api.post("/bookings/quote", {
                                hotel_id: hotel.id,
                                room_type_id: rt.id,
                                rate_plan_id: cheap?.id || null,
                                check_in: checkIn,
                                check_out: checkOut,
                                adults, children, child_ages: childAges, rooms,
                                insurance: false,
                            });
                            return [rt.id, r.data];
                        } catch (err) {
                            const detail = err?.response?.data?.detail;
                            return [rt.id, { _unavailable: true, _reason: typeof detail === "string" ? detail : "Müsaitlik yok" }];
                        }
                    })
                );
                if (cancelled) return;
                const map = {};
                for (const [rid, data] of results) {
                    if (data) map[rid] = data;
                }
                setRoomQuotes(map);
            } finally {
                if (!cancelled) setRoomQuotesLoading(false);
            }
        }, 350);
        return () => { cancelled = true; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hotel?.id, checkIn, checkOut, adults, children, JSON.stringify(childAges), rooms]);

    const selectRoomAndCheapestPlan = (rt) => {
        setSelectedRoomId(rt.id);
        const cheapest = (rt.rate_plans || []).find((p) => p.is_cheapest) || (rt.rate_plans || [])[0];
        setSelectedPlanId(cheapest?.id || null);
    };

    const goToCheckout = () => {
        if (!selectedRoom) return;
        const q = new URLSearchParams({
            hotel_id: hotel.id,
            room_type_id: selectedRoom.id,
            check_in: checkIn,
            check_out: checkOut,
            adults, children, rooms,
        });
        if (childAges.length) q.set("child_ages", childAges.join(","));
        if (selectedPlanId) q.set("rate_plan_id", selectedPlanId);
        navigate(`/checkout?${q.toString()}`);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
                <Navbar />
                <div className="container mx-auto px-4 max-w-7xl py-12">
                    <div className="h-96 rounded-2xl bg-slate-200 dark:bg-slate-800 animate-pulse mb-6" />
                    <div className="h-8 w-1/2 bg-slate-200 dark:bg-slate-800 animate-pulse mb-2" />
                    <div className="h-4 w-1/3 bg-slate-200 dark:bg-slate-800 animate-pulse" />
                </div>
            </div>
        );
    }
    if (!hotel) return <div className="p-20 text-center">Otel bulunamadı</div>;

    return (
        <div data-testid="hotel-detail-page" className="bg-slate-50 dark:bg-slate-950 min-h-screen">
            <Navbar />

            <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 mb-4 flex-col md:flex-row">
                    <div>
                        <div className="flex items-center gap-1 mb-2">
                            {[...Array(hotel.stars)].map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
                        </div>
                        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">{hotel.name}</h1>
                        <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                            <MapPin className="h-4 w-4" /> {hotel.address}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-right">
                            <div className="text-xs text-slate-500">Puan ({hotel.review_count} yorum)</div>
                            <div className="font-semibold text-slate-700 dark:text-slate-200">Mükemmel</div>
                        </div>
                        <div className="h-14 w-14 rounded-2xl bg-sky-500 text-white flex items-center justify-center font-bold text-xl">{hotel.rating}</div>
                    </div>
                </div>

                {/* Gallery */}
                <div className="grid grid-cols-4 grid-rows-2 gap-2 rounded-2xl overflow-hidden h-[280px] md:h-[480px] mb-8">
                    <div className="col-span-4 md:col-span-2 row-span-2 relative">
                        <img src={resolveImageUrl(hotel.gallery[galleryIdx] || hotel.main_image)} alt="" className="w-full h-full object-cover" data-testid="hotel-main-image" />
                        <button onClick={() => setGalleryIdx((i) => (i - 1 + hotel.gallery.length) % hotel.gallery.length)} className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white">
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button onClick={() => setGalleryIdx((i) => (i + 1) % hotel.gallery.length)} className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white">
                            <ChevronRight className="h-5 w-5" />
                        </button>
                    </div>
                    {hotel.gallery.slice(1, 5).map((g, i) => (
                        <button key={i} onClick={() => setGalleryIdx(i + 1)} className="hidden md:block overflow-hidden">
                            <img src={resolveImageUrl(g)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left content */}
                    <div className="lg:col-span-2 space-y-8">
                        {Array.isArray(hotel.active_discounts) && hotel.active_discounts.length > 0 && (
                            <section data-testid="active-discounts-section" className="p-5 rounded-2xl bg-gradient-to-br from-fuchsia-50 via-rose-50 to-amber-50 dark:from-fuchsia-950/40 dark:via-rose-950/30 dark:to-amber-950/20 border border-fuchsia-200 dark:border-fuchsia-900">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-xl">🎟️</span>
                                    <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">Geçerli Kampanyalar</h2>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {hotel.active_discounts.map((d) => {
                                        const meta = ({
                                            early_booking: { icon: "🐦", label: "Erken Rezervasyon", color: "fuchsia" },
                                            last_minute: { icon: "⏰", label: "Son Dakika", color: "rose" },
                                            long_stay: { icon: "🛏️", label: "Uzun Konaklama", color: "indigo" },
                                            date_range: { icon: "📅", label: "Sezon İndirimi", color: "sky" },
                                            promo: { icon: "🎯", label: "Promosyon", color: "emerald" },
                                        })[d.type] || { icon: "🎟️", label: "İndirim", color: "fuchsia" };
                                        const valueLabel = d.discount_type === "percentage" ? `%${Math.round(d.discount_value)}` : `${Math.round(d.discount_value)} ₺`;
                                        return (
                                            <div key={d.id} data-testid={`discount-info-${d.id}`} className={`flex items-start gap-3 p-3 rounded-xl bg-white dark:bg-slate-900 border-2 border-${meta.color}-300 dark:border-${meta.color}-800`}>
                                                <span className="text-2xl">{meta.icon}</span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-[10px] font-bold uppercase tracking-widest text-${meta.color}-600 dark:text-${meta.color}-400`}>{meta.label}</span>
                                                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold bg-${meta.color}-500 text-white`}>{valueLabel}</span>
                                                    </div>
                                                    {d.stay_start && d.stay_end && (
                                                        <div className="text-[11px] text-slate-500 mt-1">Konaklama: {d.stay_start} → {d.stay_end}</div>
                                                    )}
                                                    {d.sale_end && (
                                                        <div className="text-[11px] text-slate-500">Son rezervasyon: {d.sale_end}</div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        )}

                        <section>
                            <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white mb-3">Oda Tipleri</h2>
                            <div className="space-y-4">
                                {hotel.room_types.map((rt) => {
                                    const selected = selectedRoomId === rt.id;
                                    const plans = rt.rate_plans || [];
                                    const cheapest = plans.find((p) => p.is_cheapest) || plans[0];
                                    const cap = Number(rt.capacity || 2);
                                    const maxKids = Number(rt.max_children ?? 0);
                                    const totalGuests = adults + children;
                                    const fitsCapacity = totalGuests <= cap && (maxKids === 0 || children <= maxKids);
                                    // Prefer live quote total over static plan price
                                    const rq = roomQuotes[rt.id];
                                    const isUnavailable = !!rq?._unavailable;
                                    const totalPriceCard = rq?.total
                                        ?? ((cheapest ? (adults <= 2 ? (cheapest.price_2 || cheapest.price_1) : (cheapest.price_3 || cheapest.price_2 || cheapest.price_1)) : rt.price_per_night) * nights * rooms);
                                    const totalOriginalCard = rq?.original_subtotal && rq.original_subtotal > rq.total ? rq.original_subtotal : 0;
                                    const hasDiscount = totalOriginalCard && totalOriginalCard > totalPriceCard;
                                    const activePlanId = selected ? selectedPlanId : (cheapest?.id || null);
                                    const cardClickable = fitsCapacity && !isUnavailable;
                                    return (
                                        <div
                                            key={rt.id}
                                            data-testid={`room-card-${rt.id}`}
                                            onClick={() => { if (cardClickable) selectRoomAndCheapestPlan(rt); }}
                                            className={`p-4 rounded-2xl border-2 transition-all ${!fitsCapacity ? "border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 opacity-60 cursor-not-allowed" : isUnavailable ? "border-rose-200 dark:border-rose-900 bg-rose-50/50 dark:bg-rose-950/20 cursor-not-allowed" : selected ? "border-sky-500 bg-sky-50 dark:bg-sky-950/30 cursor-pointer" : "border-slate-200 dark:border-slate-800 hover:border-sky-300 cursor-pointer"}`}
                                        >
                                            <div className="flex flex-col md:flex-row gap-4">
                                                <img src={resolveImageUrl(rt.image)} alt={rt.name} className="w-full md:w-44 h-32 object-cover rounded-xl" />
                                                <div className="flex-1">
                                                    <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">{rt.name}</h3>
                                                    <div className="text-sm text-slate-500 mt-1">{rt.size_sqm} m² · {rt.bed_type} · {rt.capacity} kişilik{maxKids > 0 ? ` · max ${maxKids} çocuk` : ""}</div>
                                                    <div className="flex flex-wrap gap-1 mt-2">
                                                        {rt.features.slice(0, 5).map((f) => (
                                                            <span key={f} className="text-xs px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">{f}</span>
                                                        ))}
                                                    </div>
                                                    {!fitsCapacity && (
                                                        <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 text-xs font-semibold">
                                                            {totalGuests > cap ? `Kapasite aşıldı (max ${cap} kişi)` : `Bu odaya max ${maxKids} çocuk kabul edilir`}
                                                        </div>
                                                    )}
                                                    {fitsCapacity && isUnavailable && (
                                                        <div data-testid={`room-unavailable-info-${rt.id}`} className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-rose-100 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300 text-xs font-bold uppercase tracking-wide">
                                                            🚫 Satışa Kapalı{rq?._reason ? ` · ${rq._reason}` : ""}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-right md:w-44">
                                                    {!fitsCapacity ? (
                                                        <div className="text-sm font-bold text-slate-400">Uygun değil</div>
                                                    ) : isUnavailable ? (
                                                        <div className="space-y-2">
                                                            <div data-testid={`room-unavailable-badge-${rt.id}`} className="text-base font-display font-bold text-rose-600">Satışa Kapalı</div>
                                                            <div className="text-xs text-slate-500">Lütfen tarihleri değiştirin</div>
                                                            <button disabled className="w-full py-2 rounded-xl text-sm font-semibold bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed">Müsait Değil</button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {plans.length > 1 && <div className="text-[10px] uppercase tracking-widest text-slate-500">en ucuz</div>}
                                                            {hasDiscount && (
                                                                <div className="flex items-center justify-end gap-2">
                                                                    <span data-testid={`room-original-${rt.id}`} className="text-sm line-through text-slate-400">{formatTRY(totalOriginalCard)}</span>
                                                                    <span data-testid={`room-discount-pct-${rt.id}`} className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-950/50 dark:text-fuchsia-300 text-[11px] font-bold">
                                                                        -%{Math.round((totalOriginalCard - totalPriceCard) / totalOriginalCard * 100)}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            <div data-testid={`room-total-${rt.id}`} className={`text-2xl font-display font-bold ${hasDiscount ? "text-fuchsia-600" : "text-slate-900 dark:text-white"} ${roomQuotesLoading && !rq ? "opacity-60" : ""}`}>{formatTRY(totalPriceCard)}</div>
                                                            <div className="text-xs text-slate-500 mb-2">{nights} gece · {rooms} oda toplam</div>
                                                            <button
                                                                data-testid={`select-room-${rt.id}`}
                                                                onClick={(e) => { e.stopPropagation(); selectRoomAndCheapestPlan(rt); }}
                                                                className={`w-full py-2 rounded-xl text-sm font-semibold transition-colors ${selected ? "bg-sky-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-sky-100"}`}
                                                            >
                                                                {selected ? "Seçildi" : "Seç"}
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Rate plan comparison table (Faz: Yan Yana Fiyat Planı) */}
                                            {plans.length > 1 && (
                                                <div className="mt-4 -mx-4 -mb-4 rounded-b-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                                                    <RatePlanComparison
                                                        hotelId={hotel.id}
                                                        roomTypeId={rt.id}
                                                        adults={adults}
                                                        children={children}
                                                        nights={nights}
                                                        checkIn={checkIn}
                                                        onSelect={(plan) => { setSelectedRoomId(rt.id); setSelectedPlanId(plan.id); }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white mb-3">Tesis Özellikleri</h2>
                            {(() => {
                                const cat = hotel.amenities_categorized || {};
                                const totalCat = Object.values(cat).reduce((n, arr) => n + (arr?.length || 0), 0);
                                if (totalCat === 0) {
                                    return (
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="amenities-flat">
                                            {hotel.amenities.map((a) => (
                                                <div key={a} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                                                    <Check className="h-4 w-4 text-sky-500" /> {a}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                }
                                return (
                                    <div className="space-y-5" data-testid="amenities-grouped">
                                        {AMENITY_CATEGORIES.map((c) => {
                                            const list = cat[c.key] || [];
                                            if (!list.length) return null;
                                            return (
                                                <div key={c.key} data-testid={`amenity-group-${c.key}`}>
                                                    <h3 className={`text-sm font-bold uppercase tracking-wider text-${c.color}-600 dark:text-${c.color}-400 mb-2`}>{c.label}</h3>
                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                                        {list.map((a) => (
                                                            <div key={a} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                                                                <Check className={`h-4 w-4 text-${c.color}-500 flex-shrink-0`} /> <span className="truncate">{a}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </section>

                        <section>
                            <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white mb-3">Hakkında</h2>
                            <p className="text-slate-600 dark:text-slate-300 leading-relaxed">{hotel.description}</p>
                        </section>

                        <section data-testid="hotel-location-section">
                            <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white mb-3">Konum & Çevre</h2>
                            <HotelMap
                                lat={hotel.latitude}
                                lng={hotel.longitude}
                                name={hotel.name}
                                height={340}
                            />
                            <div className="mt-4">
                                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200 mb-3">Yakın Çevre</h3>
                                <NearbyPOIs hotelId={hotel.id} radius_km={60} />
                            </div>
                        </section>

                        {(() => {
                            // Real cancellation policies pulled from each rate plan (no fake defaults)
                            const policies = new Set();
                            (hotel.room_types || []).forEach((rt) => {
                                (rt.rate_plans || []).forEach((p) => {
                                    if (p.cancellation && p.cancellation.trim()) policies.add(p.cancellation.trim());
                                });
                            });
                            const list = Array.from(policies);
                            if (!list.length) return null;
                            return (
                                <section data-testid="cancellation-policies">
                                    <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white mb-3">İptal Koşulları</h2>
                                    <ul className="space-y-2">
                                        {list.map((p, i) => (
                                            <li key={i} className="flex items-start gap-2 text-slate-600 dark:text-slate-300">
                                                <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-emerald-500" />
                                                <span>{p}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            );
                        })()}

                        <section data-testid="reviews-section">
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white">Misafir Yorumları</h2>
                                <button data-testid="open-review-form" onClick={() => setReviewOpen((v) => !v)} className="px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold">
                                    {reviewOpen ? "Kapat" : "Yorum Yaz"}
                                </button>
                            </div>

                            {reviewOpen && (
                                <ReviewForm hotelId={hotel.id} onSubmitted={() => { setReviewOpen(false); }} />
                            )}

                            {(!hotel.reviews || hotel.reviews.length === 0) ? (
                                <div className="py-10 text-center border border-dashed border-slate-300 dark:border-slate-700 rounded-2xl text-slate-500 text-sm">
                                    Henüz yorum yok. <strong>İlk yorumu siz yapın!</strong>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {hotel.reviews.slice(0, 6).map((r) => (
                                        <div key={r.id} data-testid={`review-${r.id}`} className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                                            <div className="flex items-center gap-2 mb-2">
                                                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-sky-500 to-amber-400 flex items-center justify-center font-bold text-white">
                                                    {(r.guest_name || "?")[0].toUpperCase()}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="font-semibold text-slate-900 dark:text-white truncate">{r.guest_name}</span>
                                                        {r.verified && (
                                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase">
                                                                ✓ Doğrulanmış
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-slate-500">{formatDate(r.created_at)}</div>
                                                </div>
                                                <div className="px-2 py-1 rounded-md bg-sky-500 text-white text-sm font-bold">{r.rating}</div>
                                            </div>
                                            {r.title && <div className="font-semibold text-slate-700 dark:text-slate-200 text-sm mb-1">{r.title}</div>}
                                            <p className="text-sm text-slate-600 dark:text-slate-300">{r.comment}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    </div>

                    {/* Right booking sidebar */}
                    <div className="lg:col-span-1">
                        <div data-testid="booking-widget" className="sticky top-24 p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-soft space-y-4">
                            <div>
                                <div className="text-xs text-slate-500">{selectedRoomFits ? `${nights} gece · ${rooms} oda toplam` : "Bu kişi sayısı için"}</div>
                                {selectedRoomFits ? (
                                    <div className="flex items-baseline gap-2 flex-wrap">
                                        <div className={`text-3xl font-display font-bold transition-opacity ${quoting ? "opacity-50" : "opacity-100"} text-slate-900 dark:text-white`}>{formatTRY(totalPrice)}</div>
                                        {quote?.original_total && quote.original_total > quote.total && (
                                            <div className="text-sm line-through text-slate-400">{formatTRY(quote.original_total)}</div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-base font-bold text-amber-600 dark:text-amber-400">Uygun oda otomatik seçildi</div>
                                )}
                                {selectedRoomFits && quote?.per_night_avg ? (
                                    <div className="text-xs text-slate-500">{formatTRY(quote.per_night_avg)} / gece · oda</div>
                                ) : null}
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Giriş</label>
                                    <div className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800">
                                        <Calendar className="h-4 w-4 text-sky-500" />
                                        <input data-testid="detail-checkin" type="date" value={checkIn} min={today} onChange={(e) => {
                                            const newIn = e.target.value;
                                            setCheckIn(newIn);
                                            if (newIn && checkOut && new Date(checkOut) <= new Date(newIn)) {
                                                const d = new Date(newIn);
                                                d.setDate(d.getDate() + 1);
                                                setCheckOut(toLocalYMD(d));
                                            }
                                        }} className="w-full bg-transparent outline-none text-sm font-medium" />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Çıkış</label>
                                    <div className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800">
                                        <Calendar className="h-4 w-4 text-sky-500" />
                                        <input data-testid="detail-checkout" type="date" value={checkOut} min={minCheckout} onChange={(e) => setCheckOut(e.target.value)} className="w-full bg-transparent outline-none text-sm font-medium" />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-2 text-sm">
                                <Stepper testId="detail-adults" label="Yetişkin" value={adults} min={1} max={9} onChange={setAdults} />
                                <Stepper testId="detail-children" label="Çocuk" value={children} min={0} max={6} onChange={setChildren} />
                                <Stepper testId="detail-rooms" label="Oda" value={rooms} min={1} max={5} onChange={setRooms} />
                            </div>

                            {children > 0 && (
                                <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Çocukların yaşı</label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {childAges.map((age, idx) => (
                                            <div key={idx} className="flex flex-col items-center gap-0.5">
                                                <span className="text-[10px] font-semibold text-slate-500">{idx + 1}. çocuk</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="17"
                                                    value={age}
                                                    onChange={(e) => {
                                                        const v = Math.min(17, Math.max(0, Number(e.target.value)));
                                                        setChildAges((prev) => prev.map((a, i) => i === idx ? v : a));
                                                    }}
                                                    data-testid={`detail-child-age-${idx}`}
                                                    className="w-full px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-center text-sm font-bold text-slate-900 dark:text-white"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {selectedRoom && selectedRoomFits && (
                                <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-1 text-sm">
                                    <div className="flex justify-between text-slate-600 dark:text-slate-300">
                                        <span>{selectedRoom.name}</span>
                                        <span>{formatTRY(pricePerNight)} / gece</span>
                                    </div>
                                    {selectedPlan && (
                                        <div className="flex justify-between text-xs text-sky-600 dark:text-sky-400 font-semibold">
                                            <span>{selectedPlan.board || selectedPlan.name}</span>
                                            <span>{selectedPlan.is_cheapest ? "En ucuz" : "+ekstra"}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-slate-600 dark:text-slate-300">
                                        <span>{nights} gece × {rooms} oda{children > 0 ? ` · ${children} çocuk` : ""}</span>
                                        <span>{formatTRY(totalPrice)}</span>
                                    </div>
                                    {quoting && <div className="text-[10px] text-slate-400 italic">Hesaplanıyor...</div>}
                                </div>
                            )}

                            {!selectedRoomFits && (
                                <div className="pt-2 border-t border-slate-100 dark:border-slate-800 text-sm rounded-xl bg-amber-50 dark:bg-amber-950/30 p-3 border border-amber-200 dark:border-amber-900/40">
                                    <div className="font-bold text-amber-800 dark:text-amber-200">Seçili kişi sayısı için uygun oda yok</div>
                                    <div className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">Lütfen yetişkin/çocuk sayısını azaltın veya başka bir otel seçin.</div>
                                </div>
                            )}

                            {selectedRoomFits && quote?._unavailable && (
                                <div data-testid="booking-widget-unavailable" className="pt-2 border-t border-slate-100 dark:border-slate-800 text-sm rounded-xl bg-rose-50 dark:bg-rose-950/30 p-3 border border-rose-200 dark:border-rose-900/40">
                                    <div className="font-bold text-rose-800 dark:text-rose-200">🚫 Bu tarihler için satışa kapalı</div>
                                    <div className="text-xs text-rose-700 dark:text-rose-300 mt-0.5">{quote._reason || "Lütfen tarihleri değiştirin."}</div>
                                </div>
                            )}

                            <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                                <span className="font-semibold text-slate-900 dark:text-white">Toplam</span>
                                <span className={`text-2xl font-display font-bold text-sky-600 transition-opacity ${quoting ? "opacity-50" : "opacity-100"}`} data-testid="booking-widget-total">{selectedRoomFits && !quote?._unavailable ? formatTRY(totalPrice) : "—"}</span>
                            </div>

                            <button
                                data-testid="booking-reserve-btn"
                                onClick={goToCheckout}
                                disabled={!selectedRoom || nights <= 0 || !selectedRoomFits || quote?._unavailable}
                                className="w-full py-3 rounded-xl bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {quote?._unavailable ? "Müsait Değil" : "Rezervasyon Yap"}
                            </button>
                            {selectedPlan?.cancellation && (
                                <p className="text-xs text-slate-500 text-center" data-testid="plan-cancellation">{selectedPlan.cancellation}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* CMS-managed tail blocks (cross-sell, FAQ, trust, etc.) */}
            {tailBlocks.length > 0 && (
                <div data-testid="hotel-detail-tail" className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 pt-4">
                    <BlockRenderer blocks={tailBlocks} />
                </div>
            )}

            <Footer />
        </div>
    );
}

function Stepper({ label, value, min = 0, max = 99, onChange, testId }) {
    const dec = () => onChange(Math.max(min, value - 1));
    const inc = () => onChange(Math.min(max, value + 1));
    const decDisabled = value <= min;
    const incDisabled = value >= max;
    return (
        <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</label>
            <div className="flex items-center mt-0.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden">
                <button
                    type="button"
                    data-testid={`${testId}-dec`}
                    onClick={dec}
                    disabled={decDisabled}
                    aria-label={`${label} azalt`}
                    className="h-9 w-9 flex items-center justify-center text-slate-700 dark:text-slate-200 hover:bg-sky-100 dark:hover:bg-sky-900/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    <Minus className="h-3.5 w-3.5" />
                </button>
                <input
                    type="number"
                    min={min}
                    max={max}
                    value={value}
                    onChange={(e) => {
                        const v = Number(e.target.value);
                        if (!isNaN(v)) onChange(Math.min(max, Math.max(min, v)));
                    }}
                    data-testid={testId}
                    className="flex-1 w-full bg-transparent text-center font-bold text-slate-900 dark:text-white outline-none text-base [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                    type="button"
                    data-testid={`${testId}-inc`}
                    onClick={inc}
                    disabled={incDisabled}
                    aria-label={`${label} arttır`}
                    className="h-9 w-9 flex items-center justify-center text-slate-700 dark:text-slate-200 hover:bg-sky-100 dark:hover:bg-sky-900/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    <Plus className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
}

function ReviewForm({ hotelId, onSubmitted }) {
    const [name, setName] = useState("");
    const [rating, setRating] = useState(8);
    const [title, setTitle] = useState("");
    const [comment, setComment] = useState("");
    const [travelType, setTravelType] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [msg, setMsg] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || comment.trim().length < 10) {
            setMsg("Lütfen adınızı girin ve en az 10 karakterlik yorum yazın.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/hotels/${hotelId}/reviews`, {
                hotel_id: hotelId,
                guest_name: name,
                rating: Number(rating),
                title: title || null,
                comment,
                travel_type: travelType || null,
            });
            setMsg("✓ Yorumunuz alındı! Yönetici onayından sonra yayınlanacak.");
            setName(""); setTitle(""); setComment(""); setRating(8); setTravelType("");
            setTimeout(onSubmitted, 1500);
        } catch (err) {
            setMsg(err.response?.data?.detail || "Yorum gönderilemedi");
        } finally { setSubmitting(false); }
    };

    return (
        <form onSubmit={submit} data-testid="review-form" className="mb-4 p-5 rounded-2xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <input data-testid="review-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Adınız" className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm" />
                <select value={travelType} onChange={(e) => setTravelType(e.target.value)} className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm">
                    <option value="">Tatil tipi (opsiyonel)</option>
                    <option value="couple">Çift</option>
                    <option value="family">Aile</option>
                    <option value="solo">Solo</option>
                    <option value="friends">Arkadaşlar</option>
                    <option value="business">İş Seyahati</option>
                </select>
            </div>
            <div className="mb-3">
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">Puanınız: <strong className="text-sky-600">{rating}/10</strong></label>
                <input type="range" min="1" max="10" step="0.5" value={rating} onChange={(e) => setRating(e.target.value)} data-testid="review-rating" className="w-full accent-sky-500" />
            </div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Başlık (opsiyonel)" className="w-full mb-3 px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm" />
            <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Yorumunuz (en az 10 karakter)..." rows={3} className="w-full mb-3 px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm" />
            {msg && <div className={`mb-3 text-xs font-semibold ${msg.startsWith("✓") ? "text-emerald-600" : "text-red-600"}`}>{msg}</div>}
            <button type="submit" data-testid="review-submit" disabled={submitting} className="px-4 py-2 rounded-lg bg-sky-500 hover:bg-sky-600 disabled:bg-slate-300 text-white text-sm font-bold">
                {submitting ? "Gönderiliyor..." : "Yorumu Gönder"}
            </button>
        </form>
    );
}

